import { useEffect } from 'react';
import { clsx } from 'clsx';
import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { getNextStepCompliance } from '@ping/api';
import { useCompetitionsNavVisible } from '@ping/api/campaigns';
import TrendUpArrowIcon from '@ping/assets/Icon/trend-up-arrow.svg';
import ArrowRepeatIcon from '@ping/assets/Icon/arrow-repeat.svg';
import ReversedArrowsIcon from '@ping/assets/Icon/reversedArrows.svg';
import LevelArrowIcon from '@ping/assets/Icon/levelArrow.svg';
import AvatarIcon from '@ping/assets/Icon/avatar.svg';
import { Building2, Gift, Trophy } from 'lucide-react';
import HistoryIcon from '@ping/assets/Icon/history.svg';
import CardIcon from '@ping/assets/Icon/card.svg';
import LevelsIcon from '@ping/assets/Icon/levels.svg';
import LogoutIcon from '@ping/assets/Icon/logout.svg';
import PortfolioIcon from '@ping/assets/Icon/portfolio.svg';
import ReferralsIcon from '@ping/assets/Icon/referrals.svg';
import SettingsIcon from '@ping/assets/Icon/settings.svg';
import SupportIcon from '@ping/assets/Icon/support.svg';
import MarketIcon from '@ping/assets/Icon/swap.svg';
import TradingIcon from '@ping/assets/Icon/trading-limits.svg';
// import ApiKeysIcon from '@ping/assets/Icon/api.svg';
import AddressBookIcon from '@ping/assets/Icon/address-book.svg';
import { LOGIN_CALLBACK_PATH, LOGIN_PATH } from '@ping/authorization/constants';
import { handleProfile, rememberLoginOrigin } from '@ping/authorization/side-effects';
import { Avatar, LanguageCurrencyDialog } from '@ping/components';
import NavbarDropdownItems, { TNavbarDropdownItem } from '@ping/components/Header/NavbarDropdownItems';
import NavbarLink from '@ping/components/Header/NavLink';
import { PendingApprovalModal } from '@ping/components/PendingApprovalModal';
import { APP_VERSION } from '@ping/configs';
import { DEFAULT_CURRENCIES } from '@ping/constants/default-currencies.constant';
import { CAMPAIGNS_PATH, USER_COMPETITIONS_PATH } from '@ping/constants/campaigns.constant';
import { signoutReset, t } from '@ping/helpers';
import { useIsUserLoggedIn, useMobile, useSelectedCurrency } from '@ping/hooks';
import {
  languageModalStore,
  selectIsLanguageModalOpen,
  selectLanguageModalActiveTab,
  useLanguageModalStore,
} from '@ping/stores/languageModal.store';
import { importantNoticeModalRemoteStateSelector, useModalStore } from '@ping/stores/modal.store';
import {
  isSelectedThemeDarkSelector,
  selectedLanguageSelector,
  usePreferencesStore,
} from '@ping/stores/preferences.store';
import {
  businessCorePassIdSelector,
  businessNameSelector,
  isBusinessLoginSelector,
  isUserActivityStatusActiveSelector,
  isUserAdminSelector,
  isUserComplianceReviewerSelector,
  userCoreIDSelector,
  userInformationStore,
  userTierSelector,
  useUserInformationStore,
  isUserTradeDataViewerSelector,
  isUserSupportSelector,
} from '@ping/stores/userInformation.store';
import { ButtonA11Y, Toast } from '@ping/uikit';
import { LinkWithDropdown } from '@ping/components/Header/LinkWithDropdown';
import { DropDownLinkMobileModalTrigger } from '@ping/components/Header/DropDownLinkMobileModalTrigger';
import { MobileUserMenuModalTrigger } from '@ping/components/Header/MobileUserMenuModalTrigger';
import { PendingApprovalTriggerButton } from '@ping/components/Header/PendingApprovalTriggerButton';
import { ActiveBusinessReviewTriggerButton } from '@ping/components/Header/ActiveBusinessReviewTriggerButton';
import { ExchangeTypeDropdown } from '@ping/components/Header/ExchangeTypeDropdown';
import { MainSidebarMobileTrigger } from '@ping/components/MainSidebarMobileTrigger';
import { isPingCommodities, isStock, isForex, getExchangeLabel } from '@ping/utils/checkCompany';

import style from './style.module.scss';
import { PORTFOLIO_PATH } from '@ping/constants';
import { BUSINESS_VERIFICATION_PATH } from '@ping/pages/business/verification/business-entry-redirect';
import { useBusinessKybLock } from '@ping/pages/business/verification/use-business-kyb-lock';
import { truncateCoreId } from '@ping/utils';

const getTradeDropdownLinks = (): Array<{
  href: LinkProps['href'];
  title: string;
  subtitle: string;
  icon: JSX.Element;
}> => {
  const isCommoditiesMode = isPingCommodities();
  const isStockMode = isStock();
  const isForexMode = isForex();

  let simpleTradingHref: LinkProps['href'] = '/convert';
  let simpleTradingTitle = t('Convert');

  if (isCommoditiesMode) {
    simpleTradingHref = '/commodities/simple-trading' as LinkProps['href'];
    simpleTradingTitle = t('Simple trading');
  } else if (isStockMode) {
    simpleTradingHref = '/stock/simple-trading' as LinkProps['href'];
    simpleTradingTitle = t('Simple trading');
  } else if (isForexMode) {
    simpleTradingHref = '/forex/simple-trading' as LinkProps['href'];
    simpleTradingTitle = t('Simple trading');
  }

  return [
    {
      href: '/trade',
      title: isCommoditiesMode ? t('Advanced trading') : t('Spot trading'),
      subtitle: t('Trade your assets on the spot'),
      icon: <TrendUpArrowIcon />,
    },
    {
      href: simpleTradingHref,
      title: simpleTradingTitle,
      subtitle: t('Quickly convert your assets'),
      icon: <ArrowRepeatIcon />,
    },
  ];
};

const getPortfolioDropdownLinks = (): Array<{
  href: { pathname: string; query?: { [key: string]: string } } | LinkProps['href'];
  title: string;
  subtitle: string;
  icon: JSX.Element;
}> => {
  return [
    {
      href: PORTFOLIO_PATH,
      title: t('Overview'),
      subtitle: t('Take a look at your assets'),
      icon: <PortfolioIcon />,
    },
    {
      href: {
        pathname: PORTFOLIO_PATH,
        query: {
          modal: 'send_receive',
          tab: 'send',
        },
      },
      title: t('Send & Receive'),
      subtitle: t('Send or receive assets easily'),
      icon: <ReversedArrowsIcon />,
    },
    {
      href: '/user/portfolio/ramp' as LinkProps['href'],
      title: t('Buy & Sell'),
      subtitle: t('Use fiat to buy or sell crypto'),
      icon: <LevelArrowIcon />,
    },
  ];
};

export const Header = () => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const { setLanguageModal } = languageModalStore;
  const isMobile = useMobile();
  const isLMOpen = useLanguageModalStore(selectIsLanguageModalOpen);
  const LMActiveTab = useLanguageModalStore(selectLanguageModalActiveTab);
  const isSelectedThemeDark = usePreferencesStore(isSelectedThemeDarkSelector);
  const importantNoticeModal = useModalStore(importantNoticeModalRemoteStateSelector);
  const isUserActivityStatusActive = useUserInformationStore(isUserActivityStatusActiveSelector);
  const isUserComplianceReviewer = useUserInformationStore(isUserComplianceReviewerSelector);
  const isUserTradeDataViewer = useUserInformationStore(isUserTradeDataViewerSelector);
  const isUserAdmin = useUserInformationStore(isUserAdminSelector);
  const isUserSupport = useUserInformationStore(isUserSupportSelector);
  const { showCompetitionsNav } = useCompetitionsNavVisible();
  const lockedToKyb = useBusinessKybLock();
  const productNavDisabled = lockedToKyb || (isUserLoggedIn && !isUserActivityStatusActive);

  const handleGetNextStep = async () => {
    const nextStep = await getNextStepCompliance();

    userInformationStore.setComplianceStep(nextStep);
  };

  const handleLCModal = (value, activeTab?: Parameters<typeof setLanguageModal>[1]) => {
    setLanguageModal(value, activeTab);
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      handleGetNextStep();
      handleProfile();
    }
  }, [isUserLoggedIn]);

  return (
    <header className={clsx(style['header'], 'light-bb')}>
      <Navbar expand='lg'>
        <MainSidebarMobileTrigger />

        <Navbar.Brand>
          <Link
            href={
              (lockedToKyb
                ? BUSINESS_VERIFICATION_PATH
                : isUserLoggedIn && !isUserActivityStatusActive
                ? '/'
                : '/trade') as LinkProps['href']
            }
          >
            <div className={style['brand-area']}>
              <div className={style['logo-badge-wrapper']}>
                <img
                  className={clsx(style['logo'], { [style['logo--mobile']]: isMobile })}
                  src={isMobile ? '/img/logo-ping-icon.svg' : `/img/logo-${isSelectedThemeDark ? 'dark' : 'light'}.svg`}
                  alt='logo'
                />
                <img
                  className={style['app-stage-badge']}
                  src={`/img/${APP_VERSION.toLowerCase()}-badge.svg`}
                  alt={`${APP_VERSION.toLowerCase()} badge`}
                  onClick={e => {
                    e.preventDefault();
                    importantNoticeModal.open();
                  }}
                />
              </div>
              <div className={style['brand-text-v']}>
                <span>{t(getExchangeLabel())}</span>
                <span>{t('Exchange')}</span>
              </div>
            </div>
          </Link>
          {!lockedToKyb && <ExchangeTypeDropdown />}
        </Navbar.Brand>

        {!(isUserComplianceReviewer || (isUserTradeDataViewer && !isUserAdmin)) && (
          <Nav className={clsx(style['header__navbar'], style['header__links-nav'])}>
            <LinkWithDropdown
              title={t('Trade')}
              disabled={productNavDisabled || isUserSupport}
              link={getTradeDropdownLinks()}
            />
            <NavbarLink
              options={[
                {
                  title: t('Markets'),
                  href: '/markets',
                  className: style['header__link'],
                  disabled: productNavDisabled,
                },
              ]}
            />
            {showCompetitionsNav && (
              <NavbarLink
                options={[
                  {
                    title: t('Competitions'),
                    href: CAMPAIGNS_PATH,
                    className: style['header__link'],
                    disabled: productNavDisabled,
                  },
                ]}
              />
            )}
            {isUserLoggedIn && (
              <LinkWithDropdown
                title={t('Portfolio')}
                disabled={!isUserLoggedIn || lockedToKyb}
                link={getPortfolioDropdownLinks()}
              />
            )}
          </Nav>
        )}

        <Nav className={clsx(style['header__navbar'], style['nav-user'])} aria-live='polite'>
          {!isMobile && isUserLoggedIn && <PendingApprovalTriggerButton />}
          {!isMobile && isUserLoggedIn && <ActiveBusinessReviewTriggerButton />}
          {isUserLoggedIn ? <ProfileDropdownMenu /> : <GetStartedButton data-testid='login-button-nav' />}
        </Nav>
      </Navbar>

      <LanguageCurrencyDialog isOpen={isLMOpen} activeTab={LMActiveTab} onClose={() => handleLCModal(false)} />
      {isUserLoggedIn && <PendingApprovalModal />}
    </header>
  );
};

export const ProfileDropdownMenu = () => {
  const selectedCurrency = useSelectedCurrency();
  const isBusinessLogin = useUserInformationStore(isBusinessLoginSelector);
  const businessName = useUserInformationStore(businessNameSelector);
  const businessCorePassId = useUserInformationStore(businessCorePassIdSelector);
  const signedInBusiness =
    isBusinessLogin && businessName && businessCorePassId ? { businessName, businessCorePassId } : null;
  const isUserAdmin = useUserInformationStore(isUserAdminSelector);
  const isUserComplianceReviewer = useUserInformationStore(isUserComplianceReviewerSelector);
  const isUserTradeDataViewer = useUserInformationStore(isUserTradeDataViewerSelector);
  const userTier = useUserInformationStore(userTierSelector);
  const userCoreID = useUserInformationStore(userCoreIDSelector);
  const { setLanguageModal } = languageModalStore;
  const selectedLanguage = usePreferencesStore(selectedLanguageSelector);
  const isLMOpen = useLanguageModalStore(selectIsLanguageModalOpen);
  const isMobile = useMobile();
  const lockedToKyb = useBusinessKybLock();

  const handleLogout = event => {
    event.preventDefault();
    signoutReset();
  };

  if (isUserComplianceReviewer) {
    return (
      <NavDropdown
        data-testid='profile-dropdown'
        id={style['nav-dropdown-profile']}
        title={<Avatar userCoreID={userCoreID} tier='Officer' />}
      >
        <NavbarDropdownItems
          className={style['header__dropdown-item']}
          options={[
            {
              title: t('Logout'),
              href: '' as LinkProps['href'],
              icon: <LogoutIcon className={style['nav-item-icon']} />,
              className: style['header__dropdown-item'],
              onClick: handleLogout,
            },
          ]}
        />
      </NavDropdown>
    );
  }

  if (isUserTradeDataViewer) {
    return (
      <NavDropdown
        data-testid='profile-dropdown'
        id={style['nav-dropdown-profile']}
        title={<Avatar userCoreID={userCoreID} tier='TradeViewer' />}
      >
        <NavbarDropdownItems
          className={style['header__dropdown-item']}
          options={[
            {
              title: t('Logout'),
              href: '' as LinkProps['href'],
              icon: <LogoutIcon className={style['nav-item-icon']} />,
              className: style['header__dropdown-item'],
              onClick: handleLogout,
            },
          ]}
        />
      </NavDropdown>
    );
  }

  const { showCompetitionsNav } = useCompetitionsNavVisible();

  const navDropDownOptions: TNavbarDropdownItem[] = [
    ...(isMobile && signedInBusiness
      ? [
          {
            title: signedInBusiness.businessName,
            href: '' as LinkProps['href'],
            icon: <Building2 className={style['nav-item-icon']} size={20} />,
            className: clsx(style['header__dropdown-item'], style['nav-item--core-id']),
            onClick: event => event.preventDefault(),
            label: t('Business'),
          },
          {
            title: truncateCoreId(signedInBusiness.businessCorePassId),
            href: '' as LinkProps['href'],
            icon: <Building2 className={style['nav-item-icon']} size={20} />,
            className: clsx(style['header__dropdown-item'], style['nav-item--core-id']),
            onClick: event => {
              event.preventDefault();
              navigator.clipboard.writeText(signedInBusiness.businessCorePassId);
              Toast.success({ title: t('Business CoreID copied to clipboard') });
            },
            label: t('Business CoreID'),
          },
        ]
      : ([] as any)),
    ...(isMobile && userCoreID
      ? [
          {
            title: truncateCoreId(userCoreID.toUpperCase()),
            href: '' as LinkProps['href'],
            icon: <AvatarIcon className={style['nav-item-icon']} />,
            className: clsx(style['header__dropdown-item'], style['nav-item--core-id']),
            onClick: e => {
              e.preventDefault();
              navigator.clipboard.writeText(userCoreID.toUpperCase());
              Toast.success({ title: t('CoreID copied to clipboard') });
            },
            label: signedInBusiness ? t('You') : t('CoreID'),
          },
        ]
      : ([] as any)),
    ...(!isUserComplianceReviewer && !isUserTradeDataViewer && isMobile
      ? [
          {
            title: <DropDownLinkMobileModalTrigger title={t('Portfolio')} link={getPortfolioDropdownLinks()} />,
            href: '/user/portfolio',
            icon: <PortfolioIcon className={style['nav-item-icon']} />,
          },
        ]
      : ([] as any)),
    ...(showCompetitionsNav
      ? [
          {
            title: t('Competitions'),
            href: USER_COMPETITIONS_PATH as LinkProps['href'],
            icon: <Trophy className={style['nav-item-icon']} size={20} />,
          },
        ]
      : ([] as any)),
    {
      title: t('History'),
      href: '/user/history',
      icon: <HistoryIcon className={style['nav-item-icon']} />,
    },
    {
      title: t('Payments'),
      href: '/user/payments',
      icon: <CardIcon className={style['nav-item-icon']} />,
    },
    {
      title: t('Rewards'),
      href: '/user/rewards',
      icon: <Gift className={style['nav-item-icon']} size={20} />,
    },
    {
      title: t('Referrals'),
      href: '/user/referrals',
      icon: <ReferralsIcon className={style['nav-item-icon']} />,
    },
    // {
    //   title: t('API Keys'),
    //   href: '/user/api-keys',
    //   icon: <ApiKeysIcon className={style['nav-item-icon']} />,
    // },
    {
      title: t('Address Book'),
      href: '/user/addressbook',
      icon: <AddressBookIcon className={style['nav-item-icon']} />,
    },
    ...(isBusinessLogin
      ? [
          {
            title: t('Business'),
            href: '/user/business' as LinkProps['href'],
            icon: <Building2 className={style['nav-item-icon']} size={20} />,
          },
        ]
      : ([] as any)),
    {
      title: t('Settings'),
      href: '/user/settings',
      icon: <SettingsIcon className={style['nav-item-icon']} />,
    },
    {
      title: t('Support'),
      href: '/user/support',
      icon: <SupportIcon className={style['nav-item-icon']} />,
    },
    ...(isUserAdmin
      ? [
          {
            title: t('Compliance'),
            href: '/admin/compliance',
            icon: <AvatarIcon className={style['nav-item-icon']} />,
          },
          {
            title: t('Admin'),
            href: '/admin',
            icon: <AvatarIcon className={style['nav-item-icon']} />,
          },
        ]
      : ([] as any)),
    {
      title: t('Tiers'),
      href: '/user/tiers',
      icon: <LevelsIcon className={style['nav-item-icon']} />,
      label: `${t(userTier?.replace('Tier', 'TIER '))}`,
    },
    ...(!isUserComplianceReviewer && !isUserTradeDataViewer && isMobile
      ? [
          {
            title: <DropDownLinkMobileModalTrigger title={t('Trade')} link={getTradeDropdownLinks()} />,
            href: '' as LinkProps['href'],
            hasTopSeparator: true,
            icon: <TradingIcon className={style['nav-item-icon']} />,
            onClick: e => e.preventDefault(),
          },
          {
            title: t('Markets'),
            href: '/markets',
            icon: <MarketIcon className={style['nav-item-icon']} />,
            hasArrow: true,
          },
          ...(showCompetitionsNav
            ? [
                {
                  title: t('Competitions'),
                  href: CAMPAIGNS_PATH,
                  icon: <ReferralsIcon className={style['nav-item-icon']} />,
                  hasArrow: true,
                },
              ]
            : []),
        ]
      : ([] as any)),
    {
      title: selectedLanguage,
      href: '' as LinkProps['href'],
      hasTopSeparator: !isMobile,
      className: style['header__dropdown-item'],
      onClick: () => setLanguageModal(!isLMOpen),
      hasArrow: true,
    },
    {
      title: `${selectedCurrency} - ${
        DEFAULT_CURRENCIES.find(currency => currency.value === selectedCurrency.toLowerCase())?.symbol
      }`,
      href: '' as LinkProps['href'],
      className: style['header__dropdown-item'],
      onClick: () => setLanguageModal(!isLMOpen, 'currency'),
      hasArrow: true,
    },
    {
      title: t('Logout'),
      href: '' as LinkProps['href'],
      hasTopSeparator: true,
      icon: <LogoutIcon className={style['nav-item-icon']} />,
      className: style['header__dropdown-item'],
      onClick: handleLogout,
    },
  ];
  const visibleDropDownOptions = lockedToKyb
    ? navDropDownOptions.filter(item => item.onClick === handleLogout)
    : navDropDownOptions;

  return (
    <>
      {isMobile ? (
        <MobileUserMenuModalTrigger
          link={visibleDropDownOptions}
          title={
            <Avatar
              userCoreID={userCoreID}
              tier={userTier}
              businessName={signedInBusiness?.businessName}
              businessCorePassId={signedInBusiness?.businessCorePassId}
            />
          }
        />
      ) : (
        <NavDropdown
          data-testid='profile-dropdown'
          id={style['nav-dropdown-profile']}
          title={
            <Avatar
              userCoreID={userCoreID}
              tier={userTier}
              businessName={signedInBusiness?.businessName}
              businessCorePassId={signedInBusiness?.businessCorePassId}
            />
          }
        >
          <NavbarDropdownItems className={style['header__dropdown-item']} options={visibleDropDownOptions} />
        </NavDropdown>
      )}
    </>
  );
};

export const GetStartedButton = props => {
  const router = useRouter();
  const isCallbackPath = router.pathname === LOGIN_CALLBACK_PATH;

  /* The login page replaces the current URL, so capture it first to keep the return target. */
  const handlePress = () => {
    rememberLoginOrigin();
    router.push(LOGIN_PATH);
  };

  return (
    <ButtonA11Y
      {...props}
      onPress={handlePress}
      isDisabled={isCallbackPath}
      className={style['header__get-started-button']}
      primary
    >
      {t('Get Started')}
    </ButtonA11Y>
  );
};
